import styled from '@emotion/styled';
import React, { FC, useEffect, useState } from 'react';
import { CartDiscount, useCart } from '~Components/CartProvider';
import PayButton from '~Components/PayButton';
import PayWithButton from '~Components/PayWithButton';
import SplitPayment from '~Components/SplitPayment';
import BeeperSelect from '~Components/BeeperSelect';
import Modal from '~ComponentsImproved/Modal';
import useModal from '~ComponentsImproved/Modal/useModal';
import {
  useButton,
  useDeviceInfo,
  useDispatch,
  useLogger,
  useSelector,
  useStaffCan,
} from '~Hooks';
import Discounts from '~Scenes/POS/Components/Cart/Components/Discounts';
import { WhiteButton } from '~Styles';
import useI18n from '~Translations';
import Journal from '~/Utils/journal';
import { DISCOUNT_TYPE, ORDER_ORIGIN, SERVICE_TYPE } from '../../../../../__queries__';
import { useStorage } from '~Components/Storage';
import { CompleteBongQuery_venue_company_offers as Offer } from '~Scenes/POS/Queries/__queries__';
import * as actions from '~actions';
import { ORDER_STATUS } from '@baemingo/order-status-events/dist/types';

const QsrButtonsWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: 8px;
  position: relative;
  margin-top: 8px;
`;

const QsrButtonsOuterWrapper = styled.div`
  margin: 16px 12px;
`;

const PayWithWrapper = styled.div`
  position: relative;
`;

interface Props {
  venueId: string;
  offers: Offer[];
  orderId: string;
  orderWasBonged: boolean;
  defaultOrderStatus?: ORDER_STATUS;
  onComplete: () => void;
  setPaymentMethod: (id: string, poiid?: string) => void;
  isQsr?: boolean;
}

const QsrPayButtons: FC<Props> = ({
  venueId,
  offers,
  orderId,
  orderWasBonged,
  defaultOrderStatus,
  onComplete,
  setPaymentMethod,
  isQsr,
}) => {
  const logger = useLogger('payment');
  const deviceInfo = useDeviceInfo();
  const demoPopup = useModal();
  const staffCan = useStaffCan();
  const cart = useCart();
  const dispatch = useDispatch();
  const staffId = useSelector((state) => state.staff?.id);
  const { i18n } = useI18n();
  const [advancedPaymentDiscount, setAdvancedPaymentDiscount] = useState<any>(null);
  const splitPaymentModal = useModal();
  const [storage] = useStorage();

  const paymentMethods = useSelector((state) => state.POSPaymentMethods);
  const filteredPaymentMethods = paymentMethods.filter(
    (pm) => pm.venues.some((v) => v === venueId) || pm.venues.length === 0,
  );

  const beeperList = useSelector((state) => state.beeperList);
  const [showBeeperSelector, setShowBeeperSelector] = useState<boolean>(false);
  const [pendingPayment, setPendingPayment] = useState<{
    discount: CartDiscount | null;
    method: string;
    poiid?: string;
  } | null>(null);

  const demoMode = useSelector((state) => state.demoMode);
  if (demoMode) {
    venueId = window.DEMO_MODE_VENUE_ID;
  }

  const primaryPaymentMethod = filteredPaymentMethods.find((pm) => pm.isPrimary);
  const hasAdyen = filteredPaymentMethods.some((method) => method.id === 'ADYEN');
  const hasZettle = filteredPaymentMethods.some((method) => method.id === 'IZETTLE');

  // Check if there is a default payment method available
  const hasDefaultPaymentMethod =
    deviceInfo?.specificPaymentMethod || primaryPaymentMethod?.id || hasAdyen || hasZettle;

  const splitPayButton = useButton({
    onClick: () => {
      setAdvancedPaymentDiscount(cart.appliedDiscount);
      splitPaymentModal.show();
    },
    disabled: cart.items.length === 0 || !staffCan.MAKE_PAYMENT,
    disabledToast: !staffCan.MAKE_PAYMENT ? i18n('Staff.NoPermission.MakePayment') : undefined,
  });

  function pay(_appliedDiscount: CartDiscount | null, method: string, poiid?: string) {
    let discountType: DISCOUNT_TYPE | null = null;
    let discountRate: number | null = null;

    if (demoMode) {
      demoPopup.toggle();
      return;
    }

    Journal.write('Payment Button Pressed', method);

    setPaymentMethod(method, poiid);

    if (_appliedDiscount) {
      cart.setAppliedDiscount(_appliedDiscount);
      discountType = _appliedDiscount.type;
      discountRate = _appliedDiscount.value;
    }

    const messages = cart.packagedItems.filter((item) => item.isMessage);
    const comment = messages.map((message) => message.title).join(', ') || '';

    dispatch(
      // @ts-ignore
      actions.initiateOrder({
        desiredTime: cart.desiredTime || undefined,
        id: orderId,
        orderNumber: cart.easyId || '',
        serviceType: storage.enableServiceTypeDelivery ? SERVICE_TYPE.DELIVERY : cart.serviceType,
        discountType,
        discountRate,
        defaultOrderStatus,
        method,
        origin: ORDER_ORIGIN.POS,
        zoneId: storage.zoneId,
        bonged: orderWasBonged,
        staffId,
        comment,
        userId: cart.userId,
      }),
    );

    logger.info(`QSR: Initiating payment. Method: ${method}.`, {
      id: orderId,
      method,
      serviceType: cart.serviceType,
      zoneId: storage.zoneId,
      poiid,
    });
  }

  // When a beeper is selected and easyId updates, proceed with the pending payment
  useEffect(() => {
    if (pendingPayment && cart.easyId) {
      pay(pendingPayment.discount, pendingPayment.method, pendingPayment.poiid);
      setPendingPayment(null);
    }
  }, [cart.easyId]);

  // Intercept payment to show beeper selector if required and no beeper has been set
  function handlePay(discount: CartDiscount | null, method: string, poiid?: string) {
    const hasBeeper = cart.easyId && cart.easyId.includes('•');

    if (storage.requireBeeperNumber && beeperList.length > 0 && !hasBeeper) {
      setPendingPayment({ discount, method, poiid });
      setShowBeeperSelector(true);
      return;
    }

    pay(discount, method, poiid);
  }

  return (
    <>
      <QsrButtonsOuterWrapper>
        {(staffCan.MANUAL_DISCOUNT || staffCan.PRESET_DISCOUNT) && (
          <Discounts
            offers={offers ?? []}
            fullWidth
            disabled={cart.items.length === 0}
            forSelectedItems={cart.selectedItems.ids.length > 0}
          />
        )}
        <QsrButtonsWrapper>
          <WhiteButton
            {...splitPayButton}
            style={{
              height: '56px',
              gridColumn: !hasDefaultPaymentMethod ? 'span 2' : 'span 1',
            }}>
            {i18n('POS.SplitPayment')}
          </WhiteButton>
          <PayWithWrapper
            style={{
              gridColumn: !hasDefaultPaymentMethod ? 'span 2' : 'span 1',
            }}>
            <PayWithButton
              disabled={cart.items.length === 0}
              style={{
                height: hasDefaultPaymentMethod ? '56px' : '72px',
              }}
              whiteButton={Boolean(hasDefaultPaymentMethod)}
              fullButtonText
              onPay={(paymentMethodId, poiid) =>
                handlePay(cart.appliedDiscount, paymentMethodId, poiid)
              }
            />
          </PayWithWrapper>
          <PayButton
            style={{ height: '72px', gridColumn: 'span 2' }}
            disabled={cart.items.length === 0}
            onPay={(paymentMethodId, poiid) =>
              handlePay(cart.appliedDiscount, paymentMethodId, poiid)
            }
            isQsr={isQsr}
          />
        </QsrButtonsWrapper>
      </QsrButtonsOuterWrapper>

      {showBeeperSelector && (
        <BeeperSelect
          beeperList={beeperList}
          onSelect={(beeperId) => {
            cart.setEasyId(beeperId);
            setShowBeeperSelector(false);
          }}
          onCancel={() => {
            setShowBeeperSelector(false);
            setPendingPayment(null);
          }}
        />
      )}

      <Modal
        isShowing={splitPaymentModal.isShowing}
        hide={splitPaymentModal.hide}
        backdropClose={false}
        zIndex={50}>
        <SplitPayment
          orderId={orderId}
          total={cart.total - cart.discount}
          onClose={splitPaymentModal.hide}
          appliedDiscount={advancedPaymentDiscount}
          standardPay={pay}
          onComplete={onComplete}
        />
      </Modal>
    </>
  );
};

export default QsrPayButtons;
